import React, { useMemo, useState } from "react";
import { Card, DataTable, HighlightMatch, Table, hasActiveFilters, } from "@sscs/ui";
import { capitalizeFirstLetter, getQueryStatus, sortItems } from "@sscs/utils";
import { REPORT_PAGES_ROUTES } from "../../../constants";
import { ExpandedRowDetails } from "../../../ui";
import { BehaviorCategory, BehaviorMaliciousness, IssuesList, PrevalenceDetails, PrevalenceSummary, } from "../../common";
import { useReportDispatch, useReportSelector } from "../../hooks";
import { useGetComponentBehaviorQuery, useGetComponentInfoQuery, } from "../../services/fileBrowser.api";
import { filterUpdated, paginationUpdated, selectFilter, selectPagination, selectSort, sortUpdated, } from "../store/behaviorSlice";
import { useGetFileAndReportId } from "../useGetFileAndReportId";
import styles from "./behavior.module.scss";
import Filter from "./filter/Filter";
import { ID_DESCRIPTION_DELIMITER, behaviorSortSpecialCases, convertRelevance, filterBehaviors, initFilter, transformBehaviorsForDisplay, } from "./utils";
const COLUMNS = [
    { id: "info", label: "Info", width: "70px" },
    {
        id: "category",
        label: "Category",
        sortProperty: "category",
        sortId: "behavior-category-sort",
        width: "120px",
    },
    {
        id: "identifier",
        label: "Identifier",
        sortProperty: "identifier",
        sortId: "behavior-identifier-sort",
        width: "120px",
    },
    {
        id: "description",
        label: "Description",
        sortProperty: "description",
        sortId: "behavior-description-sort",
        width: "100%",
        minWidth: "130px",
    },
    {
        id: "relevance",
        label: "ML detection",
        sortProperty: "relevance",
        sortId: "behavior-relevance-sort",
        width: "160px",
    },
];
const Behavior = () => {
    const dispatch = useReportDispatch();
    const { fileId, reportId } = useGetFileAndReportId();
    const filter = useReportSelector(selectFilter);
    const sort = useReportSelector(selectSort);
    const pagination = useReportSelector(selectPagination);
    const { data: componentInfo } = useGetComponentInfoQuery({
        reportId,
        fileId: fileId !== null && fileId !== void 0 ? fileId : "",
    });
    const { data = { behavior: [] }, isLoading, isFetching, isError, error, } = useGetComponentBehaviorQuery({
        reportId: reportId,
        fileId: fileId !== null && fileId !== void 0 ? fileId : "",
    });
    const behaviors = useMemo(() => transformBehaviorsForDisplay(data.behavior, componentInfo), [data.behavior, componentInfo]);
    const [focused, setFocused] = useState(null);
    const { categories, descriptions } = useMemo(() => {
        const categories = new Set();
        const descriptions = new Set();
        data.behavior.forEach((behavior) => {
            categories.add(capitalizeFirstLetter(behavior.category));
            descriptions.add(`${behavior.identifier}${ID_DESCRIPTION_DELIMITER}${behavior.description}`);
        });
        return {
            categories: Array.from(categories),
            descriptions: Array.from(descriptions),
        };
    }, [data.behavior]);
    const filteredData = useMemo(() => filterBehaviors(behaviors, filter), [behaviors, filter]);
    const sortedData = useMemo(() => {
        return sortItems(filteredData, sort, behaviorSortSpecialCases);
    }, [filteredData, sort]);
    const paginatedData = useMemo(() => {
        return sortedData.slice((pagination.currentPage - 1) * pagination.itemsPerPage, pagination.currentPage * pagination.itemsPerPage);
    }, [sortedData, pagination]);
    const handleSortChange = (sort) => {
        dispatch(sortUpdated(sort));
    };
    return (React.createElement(React.Fragment, null,
        React.createElement(Card.Filters, { found: {
                id: "behavior-clear-all-filters",
                count: sortedData.length,
                initialFilters: initFilter,
                activeFilters: filter,
                onClear: () => dispatch(filterUpdated(initFilter)),
            } },
            React.createElement(Filter, { filter: filter, categories: categories, descriptions: descriptions, onFilterChange: (filter) => dispatch(filterUpdated(filter)) })),
        React.createElement(DataTable, { chrome: "plain", tableClassName: styles.table, columns: COLUMNS, sort: sort, onSortChange: handleSortChange, empty: {
                subject: "behaviors",
                filters: {
                    active: hasActiveFilters(initFilter, filter),
                    onReset: () => dispatch(filterUpdated(initFilter)),
                },
            }, status: getQueryStatus({
                data,
                isLoading,
                isFetching,
                isError,
                error,
            }), pagination: {
                id: "behavior-pagination",
                itemCount: sortedData.length,
                pagination,
                onChange: (pagination) => dispatch(paginationUpdated(pagination)),
            } }, paginatedData.map((behavior, index) => { var _a, _b, _c; var _d, _e, _f; return (React.createElement(DataTable.ExpandedRow, { id: `behavior-expand-row-${index}`, key: behavior.id, focused: focused === behavior.id, onClick: () => {
                setFocused(behavior.id);
            } },
            React.createElement(DataTable.ExpandedRow.Summary, null,
                React.createElement(Table.Cell, null, behavior.category ? (React.createElement(BehaviorCategory, { category: behavior.category })) : ("--")),
                React.createElement(Table.Cell, null,
                    React.createElement(HighlightMatch, { value: (_d = behavior.identifier) !== null && _d !== void 0 ? _d : "--", regex: filter.idAndDescription.split(ID_DESCRIPTION_DELIMITER)[0] })),
                React.createElement(Table.Cell, null,
                    React.createElement(HighlightMatch, { value: behavior.description, regex: (_e = filter.idAndDescription.split(ID_DESCRIPTION_DELIMITER)[1]) !== null && _e !== void 0 ? _e : filter.idAndDescription }),
                    React.createElement(PrevalenceSummary, { prevalence: behavior.prevalence })),
                React.createElement(Table.Cell, null, convertRelevance((_a = behavior.trigger_entry) === null || _a === void 0 ? void 0 : _a.relevance))),
            React.createElement(DataTable.ExpandedRow.Details, null,
                React.createElement(ExpandedRowDetails, null,
                    React.createElement(BehaviorMaliciousness, { maliciousness: behavior.maliciousness, total: behavior.total, fullWidth: true }),
                    React.createElement(PrevalenceDetails, { prevalence: behavior.prevalence, repositoryStatistics: behavior.repository_statistics, fullWidth: true }),
                    React.createElement(Card.Card, null,
                        React.createElement(Card.Title, null, "Behavior triggered by"),
                        React.createElement(Card.Content, null, (_b = behavior.trigger_entry) === null || _b === void 0 ? void 0 : _b.reasons.map((reason, index) => (React.createElement("pre", { key: index, className: styles.reason }, reason))))),
                    React.createElement(IssuesList, { violations: (_f = (_c = behavior.trigger_entry) === null || _c === void 0 ? void 0 : _c.violations) !== null && _f !== void 0 ? _f : [], to: REPORT_PAGES_ROUTES.ISSUES_ROUTE }))))); }))));
};
export default Behavior;
//# sourceMappingURL=Behavior.js.map